import React, { useMemo } from 'react'
import { Grid, Typography, Box, Button, makeStyles } from '@material-ui/core'
import { ANCHOR_LEFT } from 'react-dates/constants'
import { useTable, useGlobalFilter, usePagination, useFilters, useSortBy } from 'react-table'
import matchSorter from 'match-sorter'
import Loading from 'components/Loading'
import DateRangeFilter from 'components/filter/DateRangeFilter'
import { useDateRangeFilter } from 'filtering/hooks'
import Search from 'core/table/components/Search'
import PagedTable from 'core/table/components/PagedTable'
import DEFAULT_FILTERS from 'core/table/filters'
import { PersonNameCell, DateCell } from 'core/table/cells'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { AddTesterPrompt } from '../components/AddTesterPrompt'
import { ClearTesterPrompt } from '../components/ClearTesterPrompt'
import useGetOnMount from 'civic-champs-shared/api/hooks/useGetOnMount'

const useStyles = makeStyles(theme => ({
  addButton: {
    backgroundColor: '#ffcd00',
  },
}))

const useColumns = refresh => {
  const showDeleteTester = useShowPrompt(ClearTesterPrompt)
  return useMemo(
    () => [
      {
        id: 'createdAt',
        Header: 'Date',
        accessor: 'createdAt',
        Cell: DateCell,
        filter: 'dateRange',
      },
      {
        Header: 'Tester',
        accessor: 'person',
        Cell: PersonNameCell,
      },
      {
        Header: 'Added By',
        accessor: 'addedBy',
        Cell: PersonNameCell,
      },
      {
        Header: '',
        id: 'delete',
        Cell: ({ cell }) => (
          <Button
            variant="contained"
            color="primary"
            onClick={() => showDeleteTester({ tester: cell.row.original, refresh })}
          >
            REMOVE
          </Button>
        ),
      },
    ],
    [showDeleteTester, refresh],
  )
}

const fuzzyMatchFilter = (rows, id, filterValue) =>
  matchSorter(rows, filterValue, {
    keys: [
      'original.person.givenName',
      'original.person.familyName',
      'original.addedBy.givenName',
      'original.addedBy.familyName',
    ],
  })

fuzzyMatchFilter.autoRemove = value => !value

const filterTypes = {
  fuzzyMatch: fuzzyMatchFilter,
  ...DEFAULT_FILTERS,
}

export const TestersOverview = () => {
  const [refresh, { loading, result: testers }] = useGetOnMount({
    url: '/testers',
    errorMessage: 'Error fetching testers',
    emptyValue: [],
  })
  const classes = useStyles()
  const [dateRange, setDateRange] = useDateRangeFilter()
  const addToTesters = useShowPrompt(AddTesterPrompt)
  const table = useTable(
    {
      data: testers,
      columns: useColumns(refresh),
      filterTypes,
      globalFilter: 'fuzzyMatch',
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const handleQueryChange = query => {
    table.setGlobalFilter(query || undefined)
  }

  const handleDateRangeChange = newRange => {
    setDateRange(newRange)
    table.setFilter('createdAt', newRange)
  }

  return (
    <div>
      <Grid container direction="column" spacing={3}>
        <Grid container xs={12} item justify="space-between" alignItems="center" direction="row">
          <Grid xs={6} container alignItems="center" item>
            <Grid item>
              <Typography variant="h4">Testers</Typography>
            </Grid>
            <Grid item style={{ marginLeft: '10px' }}>
              <Button variant="contained" className={classes.addButton} onClick={() => addToTesters({ refresh, existingTesters: testers })}>
                <AddCircleIcon style={{ marginRight: 5 }} />
                Add
              </Button>
            </Grid>
          </Grid>
          <Grid xs={6} container item alignItems="center" justify="flex-end" direction="row">
            <Search onQueryChange={handleQueryChange} variant="outlined" placeholder="Search" />
            <Box style={{ padding: 4, border: 'solid 1px rgba(179, 179, 179)', borderRadius: 4, marginLeft: '10px' }}>
              <DateRangeFilter
                initialValues={dateRange}
                onFilterApplied={handleDateRangeChange}
                anchorDirection={ANCHOR_LEFT}
                noBorder={true}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid>
          <p>Orgs and events can be marked for 'testers' only, meaning that they can only be seen by them</p>
        </Grid>
        <Grid item>
          {loading ? (
            <Loading />
          ) : (
            <>
              <PagedTable {...table} />
            </>
          )}
        </Grid>
      </Grid>
    </div>
  )
}
